import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brain, Heart, Hand, Target } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import { HeroCarousel } from "@/components/HeroCarousel";
import { ProductQuickView } from "@/components/ProductQuickView";
import { SEOHead } from "@/components/seo/SEOHead";
import { OrganizationSchema, WebsiteSchema, LocalBusinessSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";
import { useReferralTracking } from "@/hooks/useReferralTracking";

const benefits = [
  {
    icon: Brain,
    title: "Increase focus",
    description: "Designed to help maintain concentration and attention for learning and daily tasks.",
  },
  {
    icon: Heart,
    title: "Manage stress and anxiety",
    description: "Tactile engagement that helps calm the mind and reduce anxious feelings.",
  },
  {
    icon: Hand,
    title: "Develop fine motor skills",
    description: "Exercise your hands and improve coordination through sensory exploration.",
  },
  {
    icon: Target,
    title: "Break unhelpful habits",
    description: "A valuable resource used by NHS, therapists, and families across the UK.",
  },
];

const products = [
  {
    name: "Samboards",
    href: "/samboards",
    description: "Hand-finished sensory boards for everyday use.",
    features: [
      "Full-size tactile board",
      "Multiple texture zones",
      "Wipe-clean surface",
      "Made in Britain"
    ]
  },
  {
    name: "Library Squares",
    href: "/library-squares",
    description: "Silent tactile tiles designed for libraries and quiet spaces.",
    features: [
      "Silent operation",
      "Variety of textures",
      "Discreet sensory support",
      "Durable construction"
    ]
  },
  {
    name: "Sensory Pads",
    href: "/sensory-pads",
    description: "Soft tactile pads for gentle, calming sensory input.",
    features: [
      "A5 size pads",
      "Multiple textures",
      "Dementia-friendly",
      "Relaxation tool"
    ]
  },
];

const testimonials = [
  {
    quote:
      "The sensory boards that Samboards have created are uniquely designed to be used for either distraction or enjoyment at home or at school.",
    author: "Healthcare Professional",
  },
  {
    quote:
      "Our son uses his small board set every day as a distraction when anxious. The quality is brilliant and it’s made a real difference.",
    author: "Parent, 2021",
  },
];

export default function Home() {
  // Track referral traffic
  useReferralTracking();

  return (
    <>
      <SEOHead
        title="Premium Tactile Sensory Boards | Handmade Sensory & Focus Tools"
        description="UK-designed premium tactile sensory boards for autistic, ADHD and neurodivergent people. Reduce anxiety, manage stress, and improve focus. Used by NHS, therapists, schools, and families."
        canonical="/"
        keywords="sensory boards, tactile tools, ADHD focus tools, autism sensory tools, anxiety management, neurodivergent support, sensory tools UK, handmade sensory boards"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }]} />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />

        <section id="hero" className="pt-8 md:pt-16 lg:pt-20 pb-32 relative hero-gradient-overlay">
          <div className="grid gap-14 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-center">
            {/* Left: Text */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-4 py-1 text-[11px] font-medium tracking-wide text-emerald-200">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Calm in motion for neurodivergent minds</span>
              </div>

              <h1 className="font-heading text-[28px] xs:text-[36px] sm:text-[48px] lg:text-[58px] leading-[1.1] page-title-gradient">
                Premium tactile <span className="block sm:inline">sensory boards</span>
                <span className="block">for neurodivergent minds & people.</span>
              </h1>

              <p className="text-sm sm:text-base md:text-lg text-slate-200/80 max-w-xl leading-relaxed">
                UK-designed sensory solutions for autistic, ADHD and neurodivergent people. Used by NHS services,
                therapists, schools, and families to reduce anxiety, manage stress, and improve focus.
              </p>

              {/* Trust line - conversion reinforcement */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-emerald-200/70">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
                  Fast UK delivery
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
                  Loved by neurodivergent families
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
                  Secure checkout
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] interactive-button text-sm sm:text-base"
                >
                  <Link to="/shop">Buy now</Link>
                </Button>

                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-emerald-300/60 bg-emerald-500/5 text-emerald-100 hover:bg-emerald-500/15 backdrop-blur-sm text-sm sm:text-base"
                >
                  <Link to="/shop">View all products</Link>
                </Button>
              </div>

              <p className="text-xs md:text-sm text-emerald-100/60">
                Designed with occupational therapists, autistic adults, and neurodivergent families across the UK.
              </p>
            </div>

            {/* Right: Hero Carousel */}
            <HeroCarousel />
          </div>
        </section>

      {/* Benefits Section */}
      <section id="benefits" className="w-full py-[90px] relative z-10">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-[32px] lg:text-[40px] mb-4 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Why Samboards?
            </h2>
            <p className="text-slate-200/80 max-w-2xl mx-auto">
              A focused range of sensory tools designed to support autistic, ADHD, and neurodivergent people in homes,
              schools, and clinical settings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.title}
                  className="benefit-card group bg-slate-900/70 border border-emerald-300/15 rounded-2xl p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)] hover:border-emerald-300/40 hover:shadow-[0_24px_70px_rgba(16,185,129,0.55)] hover:scale-[1.02] transition-all duration-300 ease-in-out cursor-pointer"
                  style={{
                    transition: 'all 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 24px 70px rgba(16, 185, 129, 0.55), 0 0 20px rgba(91, 255, 154, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 18px 45px rgba(15, 23, 42, 0.75)';
                  }}
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-300/40 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-emerald-300" />
                  </div>
                  <h3 className="font-heading text-lg mb-2 text-emerald-100">{benefit.title}</h3>
                  <p className="text-sm text-slate-200/85">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Range Section */}
      <section id="products" className="w-full py-[90px] relative z-10">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-[32px] lg:text-[40px] mb-4 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Our Product Range
            </h2>
            <p className="text-slate-200/80 max-w-3xl mx-auto">
              Premium tactile sensory boards handcrafted in the UK for different needs and environments. No stock
              images, no gimmicks – just simple, robust tools that work.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {products.map((product) => (
              <Link
                key={product.name}
                to={product.href}
                className="group relative rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-4 sm:p-5 flex flex-col justify-between interactive-card"
              >
                {/* Quick View Tooltip */}
                {product.features && product.features.length > 0 && (
                  <ProductQuickView
                    features={product.features}
                    productName={product.name}
                  />
                )}
                
                <div className="mb-3 sm:mb-4">
                  <h3 className="font-heading text-base sm:text-lg md:text-xl mb-2 text-emerald-100">{product.name}</h3>
                  <p className="text-xs sm:text-sm text-slate-200/90 leading-relaxed">{product.description}</p>
                </div>
                <span className="mt-2 inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] interactive-button"
            >
              <Link to="/shop">View all products</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Samboards Section */}
      <section id="why-choose" className="w-full py-[80px] relative z-10">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-[30px] lg:text-[36px] mb-4 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Why professionals choose Samboards
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-900/80 border border-emerald-300/20 rounded-2xl p-6 shadow-[0_18px_50px_rgba(15,23,42,0.85)]">
              <h3 className="font-heading text-xl mb-3 text-emerald-100">UK designed &amp; made</h3>
              <p className="text-sm text-slate-200/85">
                Every Samboard is carefully crafted in the UK with attention to detail, durability, and sensory
                richness.
              </p>
            </div>
            <div className="bg-slate-900/80 border border-emerald-300/20 rounded-2xl p-6 shadow-[0_18px_50px_rgba(15,23,42,0.85)]">
              <h3 className="font-heading text-xl mb-3 text-emerald-100">Trusted by professionals</h3>
              <p className="text-sm text-slate-200/85">
                Used by NHS therapists, specialist schools, care homes, and neurodivergent families across the UK.
              </p>
            </div>
            <div className="bg-slate-900/80 border border-emerald-300/20 rounded-2xl p-6 shadow-[0_18px_50px_rgba(15,23,42,0.85)]">
              <h3 className="font-heading text-xl mb-3 text-emerald-100">Evidence-informed design</h3>
              <p className="text-sm text-slate-200/85">
                Created with input from occupational therapists, autistic adults and neurodivergent individuals with
                lived experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="w-full py-[80px] pb-[110px] relative z-10">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-[30px] lg:text-[36px] mb-4 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              What people say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.author}
                className="bg-slate-900/80 border border-emerald-300/20 rounded-2xl p-7 shadow-[0_18px_55px_rgba(15,23,42,0.9)]"
              >
                <p className="text-lg text-slate-200/90 leading-relaxed mb-4 italic">“{testimonial.quote}”</p>
                <p className="font-heading text-sm text-emerald-200">– {testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
        {/* Mobile Sticky CTA - Only visible on mobile */}
        <Link to="/shop" className="mobile-sticky-cta md:hidden">
          Shop now
        </Link>
      </div>
    </>
  );
}
